import React, { useState, useEffect } from 'react';
import { Plus, X, Tag, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { apiClient } from '../App';

// Predefined color palette
const TAG_COLORS = [
  '#ef4444', // red
  '#f59e0b', // amber
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#64748b', // slate
];

export const TagBadge = ({ tag, onRemove, small = false }) => {
  const sizeClasses = small ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1';
  
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClasses}`}
      style={{ 
        backgroundColor: `${tag.color}20`, 
        color: tag.color,
        border: `1px solid ${tag.color}40`
      }}
    >
      {tag.name}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="hover:opacity-70"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};

export const TagSelector = ({ selectedTags = [], onTagsChange, availableTags = [] }) => {
  const [open, setOpen] = useState(false);
  
  const handleToggleTag = (tagId) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter(id => id !== tagId));
    } else {
      onTagsChange([...selectedTags, tagId]);
    }
  };
  
  const selectedTagObjects = availableTags.filter(t => selectedTags.includes(t.id));
  
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {selectedTagObjects.map(tag => (
          <TagBadge 
            key={tag.id} 
            tag={tag} 
            onRemove={() => handleToggleTag(tag.id)}
            small
          />
        ))}
      </div>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs">
            <Tag className="w-3 h-3 mr-1" />
            {selectedTags.length === 0 ? 'Add tags' : 'Edit tags'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2" align="start">
          <div className="space-y-1">
            {availableTags.length === 0 ? (
              <p className="text-xs text-slate-500 p-2 text-center">No tags created yet</p>
            ) : (
              availableTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => handleToggleTag(tag.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${
                    selectedTags.includes(tag.id) ? 'bg-slate-100' : ''
                  }`}
                >
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="flex-1 text-left">{tag.name}</span>
                  {selectedTags.includes(tag.id) && (
                    <span className="text-xs text-blue-600">✓</span>
                  )}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const TagManager = ({ open, onOpenChange, onTagsUpdated }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [editingTag, setEditingTag] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/tags');
      setTags(response.data);
    } catch (error) {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (open) {
      fetchTags();
    }
  }, [open]);
  
  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.post('/tags', {
        name: newTagName.trim(),
        color: newTagColor
      });
      toast.success('Tag created');
      setNewTagName('');
      setNewTagColor(TAG_COLORS[0]);
      fetchTags();
      onTagsUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };
  
  const handleUpdateTag = async () => {
    if (!editingTag || !editingTag.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    
    setSaving(true);
    try {
      await apiClient.put(`/tags/${editingTag.id}`, {
        name: editingTag.name.trim(),
        color: editingTag.color
      });
      toast.success('Tag updated');
      setEditingTag(null);
      fetchTags();
      onTagsUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteTag = async (tagId) => {
    if (!confirm('Delete this tag? It will be removed from all contacts.')) return;
    
    try {
      await apiClient.delete(`/tags/${tagId}`);
      toast.success('Tag deleted');
      fetchTags();
      onTagsUpdated?.();
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Manage Tags
          </DialogTitle>
          <DialogDescription>
            Create and manage tags to organize your contacts
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Create new tag */}
          <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
            <Label className="text-sm font-medium">Create New Tag</Label>
            <div className="flex gap-2">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Tag name"
                className="flex-1"
                data-testid="new-tag-name"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center"
                    style={{ backgroundColor: newTagColor }}
                  />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2">
                  <div className="grid grid-cols-4 gap-1">
                    {TAG_COLORS.map(color => (
                      <button
                        key={color}
                        className={`w-6 h-6 rounded ${newTagColor === color ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewTagColor(color)}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button 
                onClick={handleCreateTag} 
                disabled={saving || !newTagName.trim()}
                size="sm"
                data-testid="create-tag-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Existing tags */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Existing Tags</Label>
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : tags.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No tags created yet</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {tags.map(tag => (
                  <div 
                    key={tag.id}
                    className="flex items-center gap-2 p-2 border rounded-lg hover:bg-slate-50"
                  >
                    {editingTag?.id === tag.id ? (
                      <>
                        <Input
                          value={editingTag.name}
                          onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                          className="flex-1 h-8"
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="w-6 h-6 rounded border"
                              style={{ backgroundColor: editingTag.color }}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-2">
                            <div className="grid grid-cols-4 gap-1">
                              {TAG_COLORS.map(color => (
                                <button
                                  key={color}
                                  className={`w-6 h-6 rounded ${editingTag.color === color ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
                                  style={{ backgroundColor: color }}
                                  onClick={() => setEditingTag({ ...editingTag, color })}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button size="sm" variant="ghost" onClick={handleUpdateTag} disabled={saving}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTag(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="flex-1 text-sm">{tag.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingTag({ ...tag })}
                          className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTag(tag.id)}
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TagManager;
